import React, { useState, useEffect, useCallback } from 'react';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Search, ArrowLeft, Loader2, Eye, Tag, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CAT_COLORS = {
  getting_started: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  payments: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  api: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  billing: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  security: 'bg-red-500/15 text-red-400 border-red-500/20',
  troubleshooting: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
};

const ArticleView = ({ articleId, onBack }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kb/articles/${articleId}`);
        setArticle(data);
      } catch { toast.error('Article not found'); }
      setLoading(false);
    })();
  }, [articleId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!article) return <div className="text-center py-12 text-muted-foreground">Article not found</div>;

  const cc = CAT_COLORS[article.category] || 'bg-muted/30 text-muted-foreground';

  return (
    <div className="space-y-4" data-testid="kb-article-view">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="kb-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Articles</Button>
      <Card className="border-border bg-card">
        <CardContent className="py-6">
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${cc}`}>{article.category?.replace('_', ' ')}</span>
            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Eye className="h-3 w-3" /> {article.views || 0} views</span>
          </div>
          <h1 className="text-xl font-bold text-foreground mb-4">{article.title}</h1>
          <div className="prose prose-invert prose-sm max-w-none">
            {article.content?.split('\n').map((line, i) => {
              if (line.startsWith('## ')) return <h2 key={i} className="text-base font-semibold text-foreground mt-4 mb-2">{line.replace('## ', '')}</h2>;
              if (line.startsWith('```')) return <pre key={i} className="bg-muted/30 rounded p-3 text-xs font-mono text-foreground overflow-x-auto my-2">{line.replace(/```/g, '')}</pre>;
              if (line.startsWith('- **')) {
                const parts = line.replace('- **', '').split('**:');
                return <p key={i} className="text-sm text-muted-foreground ml-4"><span className="font-semibold text-foreground">{parts[0]}</span>:{parts[1] || ''}</p>;
              }
              if (line.match(/^\d+\./)) return <p key={i} className="text-sm text-muted-foreground ml-4">{line}</p>;
              if (line.trim() === '') return <br key={i} />;
              return <p key={i} className="text-sm text-muted-foreground leading-relaxed">{line}</p>;
            })}
          </div>
          {article.tags?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-border flex items-center gap-2 flex-wrap">
              <Tag className="h-3 w-3 text-muted-foreground" />
              {article.tags.map(t => <span key={t} className="px-2 py-0.5 rounded bg-muted/30 text-[10px] text-muted-foreground">{t}</span>)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KnowledgeBase = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');

  const fetchArticles = useCallback(async () => {
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/kb/articles`, { params });
      setArticles(data.data || []);
    } catch {}
    setLoading(false);
  }, [activeCategory, search]);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/kb/categories`); setCategories(data.categories || []); } catch {}
    })();
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  if (selectedArticle) {
    return (
      <MerchantLayout title="Knowledge Base">
        <div className="p-6 max-w-3xl mx-auto">
          <ArticleView articleId={selectedArticle} onBack={() => { setSelectedArticle(null); fetchArticles(); }} />
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout title="Knowledge Base">
      <div className="space-y-5" data-testid="kb-page">
        <p className="text-sm text-muted-foreground">Find answers to common questions</p>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search articles..." value={search} className="h-8 w-64 text-xs"
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') fetchArticles(); }} data-testid="kb-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${!activeCategory ? 'bg-indigo-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveCategory('')} data-testid="kb-cat-all">All</button>
            {categories.map(c => (
              <button key={c.value} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${activeCategory === c.value ? 'bg-indigo-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}
                onClick={() => setActiveCategory(c.value)} data-testid={`kb-cat-${c.value}`}>{c.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : articles.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No articles found</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {articles.map(a => {
              const cc = CAT_COLORS[a.category] || 'bg-muted/30 text-muted-foreground';
              return (
                <Card key={a.id} className="border-border bg-card hover:border-indigo-500/30 transition-colors cursor-pointer group"
                  onClick={() => setSelectedArticle(a.id)} data-testid={`kb-article-${a.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${cc}`}>{a.category?.replace('_', ' ')}</span>
                          {a.views > 0 && <span className="text-[10px] text-muted-foreground">{a.views} views</span>}
                        </div>
                        <h3 className="text-sm font-semibold text-foreground group-hover:text-indigo-400 transition-colors">{a.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.content_preview}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:text-indigo-400 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </MerchantLayout>
  );
};

export default KnowledgeBase;
